// Field spec driving SpecialtyRecordForm.tsx — docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md
// §8, Phase 8 (list-field repeater UI added Phase 10). Mirrors
// backend/utils/ipSpecialtyRecords.js's TEMPLATE_KEYS (the write-time
// whitelist) — change both together, same "mirror per consumer" precedent
// the rest of this codebase already uses for shapes shared across a
// network boundary (e.g. smartFields.ts/smartFields.js).
//
// `kind: 'list'` fields (implants[], transfusion[], previous_deliveries[],
// immunisation[]) address an ARRAY the same dotted-path way every other
// field addresses a scalar — SpecialtyRecordForm.tsx's existing
// getPath()/setPath() already work unchanged for a path whose value happens
// to be an array rather than a string/number/boolean, so no new addressing
// scheme was needed, only a new field-level renderer (ListFieldInput) and
// `itemFields`/`itemLabel` to describe one row. A group whose ENTIRE value
// is the list (implants, transfusion, immunisation) uses the same `key: ''`
// convention already established for a scalar top-level group
// (contraception_counselling etc.) — the list field IS the group's value,
// not a field nested inside an object. `previous_deliveries` is the one
// exception: it is itself nested inside the `obstetric` group's object, so
// it is a list-kind field alongside that group's other (scalar) fields.
import type { SpecialtyTemplate } from './types';

export type FieldKind = 'text' | 'textarea' | 'boolean' | 'number' | 'list';

export interface SpecialtyFieldSpec {
  key: string;        // dotted path under the group, e.g. "planned_procedure"
  label: string;
  kind: FieldKind;
  suffix?: string;
  itemFields?: SpecialtyFieldSpec[]; // only for kind: 'list' — the fields of one row
  itemLabel?: string;                // only for kind: 'list' — e.g. "Implant", used in "+ Add {itemLabel}"
}

export interface SpecialtyGroupSpec {
  key: string;         // top-level data key, e.g. "preop_checklist"
  label: string;
  fields: SpecialtyFieldSpec[];
}

const t = (key: string, label: string): SpecialtyFieldSpec => ({ key, label, kind: 'text' });
const ta = (key: string, label: string): SpecialtyFieldSpec => ({ key, label, kind: 'textarea' });
const b = (key: string, label: string): SpecialtyFieldSpec => ({ key, label, kind: 'boolean' });
const n = (key: string, label: string, suffix?: string): SpecialtyFieldSpec => ({ key, label, kind: 'number', suffix });
const l = (key: string, label: string, itemLabel: string, itemFields: SpecialtyFieldSpec[]): SpecialtyFieldSpec =>
  ({ key, label, kind: 'list', itemLabel, itemFields });

export const SPECIALTY_GROUPS: Record<SpecialtyTemplate, SpecialtyGroupSpec[]> = {
  surgical: [
    { key: 'preop_checklist', label: 'Pre-operative checklist', fields: [
      t('planned_procedure', 'Diagnosis / planned procedure'), b('side_site_marked', 'Side/site marked'),
      t('consent_ref', 'Consent ref (E-n)'), t('npo_since', 'NPO since'),
      ta('investigations_reviewed', 'Pre-op investigations reviewed'), b('blood_arranged', 'Blood arranged'),
      t('antibiotic_prophylaxis', 'Antibiotic prophylaxis'), b('shave_prep', 'Shave/prep done'),
      b('jewellery_removed', 'Jewellery/dentures removed'), b('id_band', 'ID band checked'), t('premedication', 'Pre-medication'),
    ] },
    { key: 'pac', label: 'Pre-anaesthetic check-up (PAC)', fields: [
      t('asa_grade', 'ASA grade'), t('airway_mallampati', 'Airway (Mallampati)'), t('airway_mouth_opening', 'Mouth opening'),
      t('airway_neck', 'Neck'), t('cardiorespiratory_status', 'Cardiorespiratory status'),
      ta('comorbid_optimisation', 'Comorbid optimisation'), ta('previous_anaesthesia_complications', 'Previous anaesthesia/complications'),
      n('fasting_hours', 'Fasting', 'h'), t('plan', 'Plan (GA/SA/Regional)'), t('fitness', 'Fitness'), t('anaesthetist', 'Anaesthetist + reg. no.'),
    ] },
    { key: 'who_checklist', label: 'WHO Surgical Safety Checklist', fields: [
      b('sign_in.done', 'Sign-in done'), t('sign_in.at', 'Sign-in time'),
      b('time_out.done', 'Time-out done'), t('time_out.at', 'Time-out time'),
      b('sign_out.done', 'Sign-out done'), t('sign_out.at', 'Sign-out time'),
      b('counts_correct.swab', 'Swab count correct'), b('counts_correct.instrument', 'Instrument count correct'), b('counts_correct.needle', 'Needle count correct'),
    ] },
    { key: 'operative_note', label: 'Operative note', fields: [
      t('date', 'Date'), t('start_time', 'Start time'), t('end_time', 'End time'),
      t('surgeon', 'Surgeon'), t('assistants', 'Assistants'), t('anaesthetist', 'Anaesthetist'), t('scrub_nurse', 'Scrub nurse'),
      t('pre_op_diagnosis', 'Pre-op diagnosis'), t('post_op_diagnosis', 'Post-op diagnosis'),
      t('procedure', 'Procedure'), t('procedure_code', 'Procedure code (HBP/ICD-PCS)'), t('anaesthesia_type', 'Anaesthesia type'),
      t('position_incision', 'Position / incision'), ta('findings', 'Findings'), ta('steps', 'Steps (telegraphic)'),
      t('specimen_sent', 'Specimen sent'), n('ebl_ml', 'EBL', 'mL'), t('fluids_given', 'Fluids given'), t('blood_given', 'Blood given'),
      ta('drains_packs_implants', 'Drains / packs / implants'), ta('complications', 'Complications'), ta('closure', 'Closure'),
    ] },
    { key: 'anaesthesia_record', label: 'Anaesthesia record (summary)', fields: [
      ta('drugs', 'Drugs'), ta('monitoring', 'Monitoring'), ta('intraop_events', 'Intra-op events'),
      t('emergence', 'Emergence'), t('pacu_aldrete_score', 'PACU Aldrete score'),
    ] },
    { key: 'post_op_orders', label: 'Post-op orders & recovery', fields: [
      t('monitoring_frequency', 'Monitoring frequency'), t('analgesia', 'Analgesia'), t('dvt_prophylaxis', 'DVT prophylaxis'),
      t('diet_progression', 'Diet progression'), t('wound_drain_care', 'Wound/drain care'), t('pacu_discharge_criteria', 'PACU discharge criteria'),
    ] },
    { key: 'implants', label: 'Implants / consumables', fields: [
      l('', 'Implants', 'implant', [t('name', 'Name'), t('make', 'Make'), t('size', 'Size'), t('batch_lot', 'Batch/lot'), t('expiry', 'Expiry'), t('udi', 'UDI')]),
    ] },
    { key: 'transfusion', label: 'Blood / blood-product transfusion', fields: [
      l('', 'Transfusions', 'transfusion', [
        t('indication', 'Indication'), t('component', 'Component'), t('bag_no', 'Bag no.'),
        t('abo_rh_patient', 'ABO/Rh (patient)'), t('abo_rh_donor', 'ABO/Rh (donor)'), t('cross_match', 'Cross-match'),
        t('start_time', 'Start time'), t('end_time', 'End time'), n('volume_ml', 'Volume', 'mL'),
        t('vitals_pre', 'Vitals (pre)'), t('vitals_during', 'Vitals (during)'), t('vitals_post', 'Vitals (post)'),
        t('reaction', 'Reaction'), t('reaction_action', 'Action taken'), t('verified_by_1', 'Verified by (1)'), t('verified_by_2', 'Verified by (2)'),
      ]),
    ] },
  ],
  obg: [
    { key: 'obstetric', label: 'Obstetric history', fields: [
      t('lmp', 'LMP'), t('edd_by_dates', 'EDD (by dates)'), t('edd_by_usg', 'EDD (by USG)'), n('gestation_weeks', 'Gestation', 'wk'),
      n('gpla.g', 'G'), n('gpla.p', 'P'), n('gpla.l', 'L'), n('gpla.a', 'A'),
      t('booking_status', 'Booking status'), t('booked_facility', 'Booked facility'),
      n('anc.visits', 'ANC visits'), t('anc.tt_tdap', 'TT/Tdap'), t('anc.iron_folate', 'Iron-folate'), t('anc.hb', 'Hb'),
      t('anc.blood_group_rh', 'Blood group / Rh'), t('anc.vdrl', 'VDRL'), t('anc.hiv', 'HIV'), t('anc.hbsag', 'HBsAg'), t('anc.ogtt', 'OGTT'), t('anc.usg', 'USG'),
      l('previous_deliveries', 'Previous deliveries', 'delivery', [
        n('year', 'Year'), t('mode', 'Mode'), t('outcome', 'Outcome'), n('weight_g', 'Weight', 'g'), t('complications', 'Complications'),
      ]),
    ] },
    { key: 'labour', label: 'Labour', fields: [
      b('admitted_in_labour', 'Admitted in labour'), t('membranes', 'Membranes'), ta('partograph_summary', 'Partograph summary (WHO modified)'),
    ] },
    { key: 'delivery', label: 'Delivery', fields: [
      t('mode', 'Mode'), t('date_time', 'Date/time'), t('indication', 'Indication'), b('episiotomy', 'Episiotomy'),
      t('placenta', 'Placenta'), n('blood_loss_ml', 'Blood loss', 'mL'), t('oxytocin', 'Oxytocin'),
    ] },
    { key: 'postnatal', label: 'Postnatal', fields: [
      t('uterine_tone', 'Uterine tone'), t('lochia', 'Lochia'), t('breast', 'Breast'), t('perineum', 'Perineum'),
    ] },
    { key: 'newborn', label: 'Newborn record', fields: [
      t('sex', 'Sex'), n('weight_g', 'Weight', 'g'), n('apgar_1', 'Apgar 1 min'), n('apgar_5', 'Apgar 5 min'), n('apgar_10', 'Apgar 10 min'),
      t('resuscitation', 'Resuscitation'), t('cord_care', 'Cord care'), b('vitamin_k', 'Vitamin K given'),
      b('bcg', 'BCG given'), b('opv0', 'OPV-0 given'), b('hepb0', 'HepB-0 given'),
      t('feeding', 'Feeding'), t('jaundice', 'Jaundice'), n('discharge_weight_g', 'Discharge weight', 'g'), t('own_uhid', 'Own UHID (if admitted)'),
    ] },
    { key: 'gynaecology', label: 'Gynaecology', fields: [
      ta('menstrual_history', 'Menstrual history (detail)'), t('pap_hpv', 'PAP / HPV'), ta('pelvic_exam', 'Pelvic exam'),
    ] },
    { key: 'mtp', label: 'MTP (restricted category — redacted outside MRD/Legal and the patient)', fields: [
      t('gestation', 'Gestation'), ta('indication', 'Indication'), t('register_no', 'MTP register no.'),
    ] },
    { key: 'contraception_counselling', label: 'Contraception counselling', fields: [
      { key: '', label: 'Notes', kind: 'textarea' },
    ] },
  ],
  paediatric: [
    { key: 'growth', label: 'Growth', fields: [
      n('weight_kg', 'Weight', 'kg'), n('height_cm', 'Height', 'cm'), n('head_circumference_cm', 'Head circumference', 'cm'),
      t('weight_pctile', 'Weight percentile'), t('height_pctile', 'Height percentile'), t('hc_pctile', 'HC percentile'), t('bmi_pctile', 'BMI percentile'),
    ] },
    { key: 'birth_history', label: 'Birth history', fields: [
      n('gestation_weeks', 'Gestation', 'wk'), t('mode', 'Mode of delivery'), n('birth_weight_g', 'Birth weight', 'g'),
      b('nicu_stay', 'NICU stay'), b('neonatal_jaundice', 'Neonatal jaundice'),
    ] },
    { key: 'immunisation', label: 'Immunisation history', fields: [
      l('', 'Immunisations', 'vaccine', [t('vaccine', 'Vaccine'), t('date', 'Date'), b('card_seen', 'Card seen')]),
    ] },
    { key: 'milestones', label: 'Developmental milestones', fields: [
      t('gross_motor', 'Gross motor'), t('fine_motor', 'Fine motor'), t('language', 'Language'), t('social', 'Social'),
    ] },
    { key: 'feeding', label: 'Feeding / diet', fields: [
      t('type', 'Type (breast/formula/complementary)'), ta('details', 'Details'),
    ] },
    { key: 'hydration', label: 'Hydration & fluid calculation', fields: [
      t('status', 'Hydration status'), n('fluid_maintenance_ml', 'Maintenance', 'mL/day'), n('fluid_deficit_ml', 'Deficit', 'mL'),
    ] },
    // These two are scalar TOP-LEVEL `data` keys, not objects with their own
    // sub-fields (see ipSpecialtyRecords.js's TEMPLATE_KEYS) — a single
    // field whose own `key` is `''` means "this group's value IS the field",
    // not "a field nested inside the group's object". SpecialtyRecordForm.tsx
    // reads/writes `data[group.key]` directly for that case.
    { key: 'guardian_consent_confirmed', label: 'Guardian consent (mandatory < 18y)', fields: [
      { key: '', label: 'Confirmed', kind: 'boolean' },
    ] },
    { key: 'newborn_screen_results', label: 'Newborn screening results', fields: [
      { key: '', label: 'Results', kind: 'textarea' },
    ] },
  ],
};
